import { useState } from 'react';
import { createContainer } from 'unstated-next';
import { Fraction, fraction } from 'mathjs';
import * as types from './types'
import { NUM_BGM_LANES, GRID_EDIT_DIV } from '../components/lane';
import { EditContainer } from './EditContainer';
import * as math from 'mathjs';
import fumen from '../fumen.json';

export const jsonToSequence = (json: any) => ({
  keyNotes: json.keyNotes.map((n: any) => ({
    ...n,
    timePos: fraction(n.timePos.n, n.timePos.d) as Fraction
  })) as types.KeyNote[],
  nextKeyNoteId: json.nextKeyNoteId as number,
  trackNotesList: json.trackNotesList.map((nts: any) => nts.map((n: any) => ({
    ...n,
    timePos: fraction(n.timePos.n, n.timePos.d) as Fraction
  }))) as types.TrackNote[][],
  trackNextIds: json.trackNextIds as number[]
});

export const SequenceContainer = createContainer(() => {
  const edit = EditContainer.useContainer();

  // const [keyNotes, setKeyNotes] = useState([] as types.KeyNote[]);
  // const [nextKeyNoteId, setNextKeyNoteId] = useState(0);
  // const [trackNotesList, setTrackNotesList] = useState<types.TrackNote[][]>(Array(NUM_BGM_LANES).fill([]));
  // const [trackNextIds, setTrackNextIds] = useState<number[]>(Array(NUM_BGM_LANES).fill(0));
  const jsonSeq = jsonToSequence(fumen);
  const [keyNotes, setKeyNotes] = useState(jsonSeq.keyNotes);
  const [nextKeyNoteId, setNextKeyNoteId] = useState(jsonSeq.nextKeyNoteId);
  const [trackNotesList, setTrackNotesList] = useState(jsonSeq.trackNotesList);
  const [trackNextIds, setTrackNextIds] = useState(jsonSeq.trackNextIds);
  const [backupKeyNotes, setBackupKeyNotes] = useState([] as types.KeyNote[]);

  // TODO: トラックの削除の際に選択トラックがそのトラックなら選択トラックを変える

  // アクション

  const clearAll = () => {
    setKeyNotes([]);
    setNextKeyNoteId(0);
    setTrackNotesList(Array(NUM_BGM_LANES).fill([]));
    setTrackNextIds(Array(NUM_BGM_LANES).fill(0));
  }

  const clearKey = () => {
    setKeyNotes([]);
    setNextKeyNoteId(0);
  }

  const reduceAddKeyNotes = (source: types.KeyNote[], track: number, timePosList: Fraction[], lane: number)
    : [types.KeyNote[], number] => [
      source.filter(n => n.track !== track || !timePosList.some(t => math.equal(t, n.timePos))).concat(
        timePosList.map((timePos, i) => ({
          isKey: true,
          id: nextKeyNoteId + i,
          track,
          timePos,
          lane,
        }))
      ),
      nextKeyNoteId + timePosList.length
    ];

  const reduceAddKeyNotesLane = (source: types.KeyNote[], track: number, addNotes: { timePos: Fraction, lane: number }[])
    : [types.KeyNote[], number] => [
      source.filter(n => n.track !== track || !addNotes.some(a => math.equal(a.timePos, n.timePos))).concat(
        addNotes.map((note, i) => ({
          isKey: true,
          id: nextKeyNoteId + i,
          track,
          timePos: note.timePos,
          lane: note.lane,
        }))
      ),
      nextKeyNoteId + addNotes.length
    ];

  const addKeyNotes = (track: number, timePosList: Fraction[], lane: number) => {
    const [notes, id] = reduceAddKeyNotes(keyNotes, track, timePosList, lane);
    setKeyNotes(notes);
    setNextKeyNoteId(id);
  };

  const addKeyNotesRange = (track: number, from: Fraction, to: Fraction, lane: number) => {
    const timePosList = [];

    for (const note of trackNotesList[track]) {
      if (math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        timePosList.push(note.timePos);
      }
    }

    addKeyNotes(track, timePosList, lane);
  };

  const deleteKeyNotes = (ids: number[]) =>
    setKeyNotes(
      keyNotes.filter(note => !ids.includes(note.id))
    );

  const deleteKeyNotesRange = (track: number, from: Fraction, to: Fraction, lane: number) => {
    const ids = [];

    for (const note of keyNotes) {
      if (note.lane === lane && note.track === track && math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        ids.push(note.id);
      }
    }

    deleteKeyNotes(ids);
  };

  const addTrackNotes = (track: number, timePosList: Fraction[]) => {
    setTrackNotesList(
      trackNotesList.map((notes, i) =>
        i === track ?
          notes.filter(n => !timePosList.some(t => math.equal(t, n.timePos))).concat(
            timePosList.map((timePos, j) => ({
              isKey: false,
              id: trackNextIds[i] + j,
              track,
              timePos,
            }))
          ) :
          notes
      )
    );
    setTrackNextIds(trackNextIds.map((id, i) =>
      i === track ? id + trackNextIds.length : id
    ));
  };

  const addTrackNotesRange = (track: number, from: Fraction, to: Fraction) => {
    const timePosList = [];

    for (let pos = from; math.smallerEq(pos, to); pos = math.add(pos, fraction(1, GRID_EDIT_DIV)) as Fraction) {
      timePosList.push(pos);
    }

    addTrackNotes(track, timePosList);
  };

  const deleteTrackNotes = (track: number, ids: number[]) =>
    setTrackNotesList(trackNotesList.map((notes, i) =>
      i === track ?
        notes.filter(note =>
          !ids.includes(note.id)
        ) :
        notes
    ));

  const deleteTrackNotesRange = (track: number, from: Fraction, to: Fraction) => {
    const ids = [];

    for (const note of trackNotesList[track]) {
      if (math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        ids.push(note.id);
      }
    }

    deleteTrackNotes(track, ids);
  };

  const startApplyTool = (track: number, timePos: Fraction, lane: number) => {
    setBackupKeyNotes(keyNotes);
    const [newKeyNotes, newId] = reduceAddKeyNotes(keyNotes, track, [timePos], lane);
    setKeyNotes(newKeyNotes);
    setNextKeyNoteId(newId);

    // ツール適用のためにソートする
    setTrackNotesList(trackNotesList.map((notes, i) =>
      i === track ?
        [...notes].sort((a, b) => math.compare(a.timePos, b.timePos) as number) :
        notes
    ));
  };

  // これは from > to を許容する
  const applyTrill = (track: number, from: Fraction, to: Fraction, lanes: [number, number]) => {
    const addNotes: { timePos: Fraction, lane: number }[] = [];
    let trackNotes = trackNotesList[track];
    if (math.larger(from, to)) {
      trackNotes = [...trackNotesList[track]].reverse();
      [from, to] = [to, from];
    }
    let count = 0;
    for (const note of trackNotes) {
      if (math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        addNotes.push({ timePos: note.timePos, lane: lanes[count % 2] });
        count++;
      }
    }

    const [notes, id] = reduceAddKeyNotesLane(backupKeyNotes, track, addNotes);
    setKeyNotes(notes);
    setNextKeyNoteId(id);
  }

  // これは from > to を許容する
  const applyStairs = (track: number, from: Fraction, to: Fraction, startLane: number, direction: number) => {
    const addNotes: { timePos: Fraction, lane: number }[] = [];
    let trackNotes = trackNotesList[track];
    if (math.larger(from, to)) {
      trackNotes = [...trackNotesList[track]].reverse();
      [from, to] = [to, from];
    }
    let lane = startLane;
    for (const note of trackNotes) {
      if (math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        addNotes.push({ timePos: note.timePos, lane });
        if (lane !== 0 && direction !== 0) {
          if (lane === 1) direction = 1;
          if (lane === 7) direction = -1;
          lane += direction;
        }
      }
    }

    const [notes, id] = reduceAddKeyNotesLane(backupKeyNotes, track, addNotes);
    setKeyNotes(notes);
    setNextKeyNoteId(id);
  }

  // これは from > to を許容する
  const applyMonoStairs = (track: number, from: Fraction, to: Fraction, startLane: number, direction: number) => {
    const addNotes: { timePos: Fraction, lane: number }[] = [];
    let trackNotes = trackNotesList[track];
    if (math.larger(from, to)) {
      trackNotes = [...trackNotesList[track]].reverse();
      [from, to] = [to, from];
    }
    let lane = startLane;
    for (const note of trackNotes) {
      if (math.smallerEq(from, note.timePos) && math.smallerEq(note.timePos, to)) {
        addNotes.push({ timePos: note.timePos, lane });
        if (lane !== 0) {
          if (lane % 2 === 0) {
            lane = ((lane / 2 - 1 + 3 + direction) % 3 + 1) * 2;
          } else {
            lane = ((lane - 1) / 2 + 4 + direction) % 4 * 2 + 1;
          }
        }
      }
    }

    const [notes, id] = reduceAddKeyNotesLane(backupKeyNotes, track, addNotes);
    setKeyNotes(notes);
    setNextKeyNoteId(id);
  }

  return {
    edit,
    keyNotes, setKeyNotes, trackNotesList, setTrackNotesList, clearAll, clearKey,
    nextKeyNoteId, setNextKeyNoteId, trackNextIds, setTrackNextIds,
    addKeyNotes, addKeyNotesRange, deleteKeyNotes, deleteKeyNotesRange,
    addTrackNotes, addTrackNotesRange, deleteTrackNotes, deleteTrackNotesRange,
    startApplyTool, applyStairs, applyMonoStairs, applyTrill,
  };
});
